/**
 *
 * MobileParentHeader
 *
 */

import React, {useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { compose } from 'redux';
import classNames from 'classnames';

// styles
import './style.scss';

// antd component

// assets
import CustomIcons from 'components/CustomIcons';

// components
import ChildPreview from 'mobile/components/ChildPreview';

import history from 'utils/history';

// global user
import {
  withUser,
  useGetUserChildren,
  useChangeChildByID,
  useGetLocalUser,
  useGetLocalActiveChild
} from 'engine/Contexts/User.context';

// hooks
import { useAuthRole } from 'utils/hooks';

function MobileParentHeader({
  visibility,
  collapse,
  roles,
  user,
  hideForPath,
  // default props
  className,
}) {
  
  const localUser = useGetLocalUser()();
  const activeChild = useGetLocalActiveChild()();
  const parentChildren = useGetUserChildren()();
  const changeChildByID = useChangeChildByID();

  const roleClass = localUser?.role ? localUser?.role : '';

  const containerRef = useRef(null);
  const activeItemRef = useRef(null);

  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScrollPosition, setMaxScrollPosition] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);

  const MIN_DISTANCE = 5;
  const CONTAINER_PADDING = 15;
  const CONTAINER_SCROLL_STEP = 100;

  const childClassNames = classNames(
    'layout-header-parent',
    roleClass,
    className,
  );

  // If pathname is changes than we scroll to the active list item
  useEffect(() => {
    if (!!containerRef?.current && !!activeItemRef?.current) {
      containerRef.current.scrollTo({
        left: activeItemRef?.current?.offsetLeft - CONTAINER_PADDING,
        behavior: "smooth",
      });
    }
  }, [history?.location?.pathname]);


  // Init scrollbar container and set max positin for scrolling
  useEffect(() => {
    if (!!containerRef?.current) {
      setMaxScrollPosition(
        containerRef?.current?.scrollWidth - containerRef?.current?.clientWidth
      );
      return () => {
        setIsDragging(false);
      }
    } 
  }, [containerRef]);

  useEffect(() => {
    setIsDragging(loading);
  }, [loading]);

  const onMountedItemList = useCallback((ref) => {
    activeItemRef.current = ref?.current || null;
    if (!!containerRef?.current && !!ref?.current && !isDragging) {
      containerRef.current.scrollTo({
        left: ref?.current?.offsetLeft - CONTAINER_PADDING,
        behavior: "smooth",
      });
    }
  }, [activeItemRef, isDragging]);

  const handleContainerScroll = (e) => {
    setIsDragging(true);
    setScrollPosition(e.target.scrollLeft);
    setMaxScrollPosition(
      e.target.scrollWidth - e.target.clientWidth
    );
  };

  const handleChildClick = async (childID, index) => {
    const result = await changeChildByID(childID);
    !!result && setLoading(false);
  };
  
  const handleTouchStart = (e) => {
    setIsDragging(false);
    const touchStart = e.touches[0].clientX;
    setTouchStartX(touchStart);
  };
  
  const handleTouchMove = (e) => {
    setIsDragging(true);
    const touchEnd = e.touches[0].clientX;
    setTouchEndX(touchEnd);
  };
  
  const handleTouchEnd = (e) => {
    
   // const distance = e.changedTouches[0].clientX - touchStartX;
    const distance = touchEndX - touchStartX;
    if (distance < MIN_DISTANCE) {
      // click
      // console.log("Click", isDragging, distance);
    } else {
      // swipe
      // console.log("Swipe", isDragging, distance);
      if (distance > 0) {
        !!containerRef?.current && containerRef?.current.scrollTo({
          left: scrollPosition - CONTAINER_SCROLL_STEP,
          behavior: "smooth",
        });
      } else {
        !!containerRef?.current && containerRef?.current.scrollTo({
          left: scrollPosition + CONTAINER_SCROLL_STEP,
          behavior: "smooth",
        });
      }
      setIsDragging(false);
    }
  };

  const handleArrowClick = (direction) => {
    setIsDragging(true);
    if (direction === 'left') {
      !!containerRef?.current && containerRef?.current.scrollTo({
        left: scrollPosition - CONTAINER_SCROLL_STEP,
        behavior: "smooth",
      });
    } else {
      !!containerRef?.current && containerRef?.current.scrollTo({
        left: scrollPosition + CONTAINER_SCROLL_STEP,
        behavior: "smooth",
      });
    }
    setIsDragging(false);
  };

  // check if open page for online
  // if detail page => is on open page except community detail page
  const isPublicPath = useCallback(() => {
    const pathname = window.location.pathname;
    const isPath = hideForPath?.some(p => pathname.includes(p));
    if (!!isPath) {
      return true;
    } else {
      return false;
    }
  }, []);

  const addNewChild = useMemo(() => (
    <div
      className="add-new-child child-qr"
      onClick={() => {
        !isDragging && history.push({
          pathname: `/linkstudent`,
        });
      }}
    >
      <CustomIcons type="qrcode" />
    </div>
  ), []);

  const renderList = useMemo(() => {
    const child = user?.activeChild || activeChild;
    return !!parentChildren ? (
      <div
        className="mobile-child-menu"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onScroll={handleContainerScroll}
        ref={containerRef}
      >
        {parentChildren?.map((student, index) => !!student?._id && (
            <ChildPreview 
              key={`side-menu-std-info-${student?._id}`}
              view="small"
              showChangeButton={false}
              loading={loading}
              isActive={student?._id == child?._id}
              child={student}
              onClick={() => {
                setLoading(true);
                handleChildClick(student?._id, index)
              }}
              onMount={(ref) => onMountedItemList(ref)}
            />
          ))}
        {addNewChild}
      </div>
    ) : (
      <div className="mobile-child-menu" ref={containerRef}>
        {addNewChild}
      </div>  
    )
  }, [user?.activeChild, activeChild, user?.students, containerRef, parentChildren])



  return !!useAuthRole(roles) && visibility && !isPublicPath() && (
    <div className={classNames(
      collapse && 'collapsed',
      loading && 'loading',
      childClassNames
    )}>
      <span
        className={classNames(
          "horizontal-menu-arrow left",
          scrollPosition === 0 && 'hide'
        )}
        onClick={() => handleArrowClick('left')}
      ></span>
    
      {renderList}  

      <span
        className={classNames(
          "horizontal-menu-arrow right",
          scrollPosition === maxScrollPosition && "hide"
        )}
        onClick={() => handleArrowClick('right')}
      ></span>
    </div>
  );
}

MobileParentHeader.defaultProps = {
  visibility: true,
  collapse: false,
  roles: ["parent"],
  hideForPath: ['/signupparent', '/reset', '/confirm', '/linkstudent', '/login']
};
MobileParentHeader.propTypes = {};

export default compose(
  withUser,
)(MobileParentHeader);
